import { cloneElement, isValidElement } from "react";
import PropTypes from "prop-types";

import Flx from "../layout/Flx";
import Htag from "../typography/Htag";
import Txt from "../typography/Txt";

/**
 * Reusable empty-state placeholder for tabs/sections with no content.
 *
 * @param {object} props
 * @param {React.ReactElement} [props.icon] - Icon element (MUI icon etc); sized/colored by the component.
 * @param {React.ReactNode} props.title - Headline.
 * @param {React.ReactNode} [props.description] - Supporting copy.
 * @param {React.ReactNode} [props.actions] - Buttons/action elements rendered below the description.
 * @param {number} [props.maxWidth=460] - Max width for description text.
 * @param {number|string} [props.py=8] - Vertical padding (MUI spacing units).
 * @param {object} [props.sx] - Root style overrides.
 */
const EmptyStatePlaceholder = ({
  icon,
  title,
  description,
  actions,
  maxWidth = 460,
  py = 8,
  sx,
}) => {
  const sizedIcon =
    isValidElement(icon)
      ? cloneElement(icon, {
          sx: { fontSize: 40, opacity: 0.5, ...(icon.props?.sx || {}) },
        })
      : icon;

  return (
    <Flx column center gap={1.5} sx={{ py, px: 2, color: "#64748b", ...sx }}>
      {sizedIcon}
      {title && (
        // A section headline, not a page one: 15px / 600 by default (the h2
        // variant's size read as a page title inside a card).
        <Htag h2 sx={{ fontSize: 15, fontWeight: 600, color: "#334155" }}>
          {title}
        </Htag>
      )}
      {description && (
        <Txt
          sx={{
            color: "#64748b",
            textAlign: "center",
            maxWidth,
          }}
        >
          {description}
        </Txt>
      )}
      {actions && <Flx ac gap={1} sx={{ mt: 1 }}>{actions}</Flx>}
    </Flx>
  );
};

EmptyStatePlaceholder.propTypes = {
  icon: PropTypes.node,
  title: PropTypes.node,
  description: PropTypes.node,
  actions: PropTypes.node,
  maxWidth: PropTypes.number,
  py: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  sx: PropTypes.object,
};

export default EmptyStatePlaceholder;
